import { useState, useMemo } from 'react';
import { t } from '@/lib/i18n/messages';
import { WizardStepRewardsJuthoor } from './wizard-step-rewards-juthoor';
import { WalkingPathMap } from './walking-path-map';
import type { Locale } from '@/types/auth';
import type { CreateInitiativeRewardsInput } from '@/types/initiative';
import type { JuthoorRewardsInput } from '@/types/juthoor';

interface WizardStepRewardsProps {
  locale: Locale;
  initiativeType?: string;
  initialData?: Partial<CreateInitiativeRewardsInput>;
  cityId?: string;
  onValidSubmit: (data: CreateInitiativeRewardsInput, initBudget: number) => void;
  onJuthoorSubmit?: (data: JuthoorRewardsInput) => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

interface FormErrors {
  start_date?: string;
  end_date?: string;
  goal?: string;
  goal_time?: string;
  reward?: string;
  nc?: string;
  walking_area_type?: string;
  walking_area?: string;
}

type DurationShortcut = '1m' | '6m' | '1y' | 'custom';

const durationShortcuts: DurationShortcut[] = ['1m', '6m', '1y', 'custom'];

function getDurationDays(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.max(0, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}

function addMonths(dateStr: string, months: number): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0] ?? '';
}

function today(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

export function WizardStepRewards({
  locale,
  initiativeType = '',
  initialData,
  cityId = '',
  onValidSubmit,
  onJuthoorSubmit,
  onBack,
  isSubmitting = false,
}: WizardStepRewardsProps) {
  const isRTL = locale === 'ar';

  // Shared date state — lifted here so sub-components can reuse
  const [durationType, setDurationType] = useState<'fixed' | 'open'>(
    initialData?.duration_type || 'fixed'
  );
  const [startDate, setStartDate] = useState(initialData?.start_date || today());
  const [endDate, setEndDate] = useState(initialData?.end_date || '');

  // Dispatch to type-specific sub-components
  if (initiativeType === 'juthoor_initiative') {
    return (
      <WizardStepRewardsJuthoor
        locale={locale}
        onValidSubmit={onJuthoorSubmit ?? (() => {})}
        onBack={onBack}
        isSubmitting={isSubmitting}
      />
    );
  }

  // Default form: Walk
  return (
    <WalkLikeRewardsForm
      locale={locale}
      isRTL={isRTL}
      initiativeType={initiativeType}
      initialData={initialData}
      durationType={durationType}
      setDurationType={setDurationType}
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
      onValidSubmit={onValidSubmit}
      onBack={onBack}
    />
  );
}

// ─── Default form (Walk-like types) ────────────────────────────────────────

interface WalkFormProps {
  locale: Locale;
  isRTL: boolean;
  initiativeType: string;
  initialData?: Partial<CreateInitiativeRewardsInput>;
  durationType: 'fixed' | 'open';
  setDurationType: (v: 'fixed' | 'open') => void;
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  onValidSubmit: (data: CreateInitiativeRewardsInput, initBudget: number) => void;
  onBack: () => void;
}

function WalkLikeRewardsForm({
  locale,
  isRTL,
  initiativeType,
  initialData,
  durationType,
  setDurationType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onValidSubmit,
  onBack,
}: WalkFormProps) {
  const showNc = true;
  const isWalk = initiativeType === 'walk_initiative';

  const [activeShortcut, setActiveShortcut] = useState<DurationShortcut>('custom');
  const [goal, setGoal] = useState(initialData?.goal?.toString() || '');
  const [goalTime, setGoalTime] = useState(initialData?.goal_time || 7);
  const [reward, setReward] = useState(initialData?.reward?.toString() || '');
  const [nc, setNc] = useState(initialData?.nc?.toString() || '');
  const [walkingAreaType, setWalkingAreaType] = useState(initialData?.walking_area_type || '');
  const [walkingPaths, setWalkingPaths] = useState<{ lat: number; lng: number }[][]>([]);
  const [estimatorMode, setEstimatorMode] = useState<'byUsers' | 'byBudget'>('byUsers');
  const [estimatorInput, setEstimatorInput] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  const handleShortcut = (shortcut: DurationShortcut) => {
    setActiveShortcut(shortcut);
    if (shortcut === '1m') setEndDate(addMonths(startDate, 1));
    else if (shortcut === '6m') setEndDate(addMonths(startDate, 6));
    else if (shortcut === '1y') setEndDate(addMonths(startDate, 12));
  };

  const durationDays = useMemo(
    () => (durationType === 'fixed' ? getDurationDays(startDate, endDate) : 365),
    [durationType, startDate, endDate]
  );

  const budgetEstimate = useMemo(() => {
    const inputNum = parseFloat(estimatorInput) || 0;
    const rewardNum = parseFloat(reward) || 0;
    const periods =
      goalTime > 0 && durationDays > 0 ? Math.floor(durationDays / goalTime) : 0;

    if (periods === 0 || rewardNum === 0 || inputNum === 0) {
      return { estimatedCp: 0, estimatedNc: 0, maxParticipants: 0, periods };
    }

    if (estimatorMode === 'byUsers') {
      const estimatedCp = inputNum * rewardNum * periods;
      return {
        estimatedCp,
        estimatedNc: estimatedCp * 0.01,
        maxParticipants: inputNum,
        periods,
      };
    } else {
      const maxParticipants =
        rewardNum > 0 ? Math.floor(inputNum / (rewardNum * periods)) : 0;
      return {
        estimatedCp: inputNum,
        estimatedNc: inputNum * 0.01,
        maxParticipants,
        periods,
      };
    }
  }, [estimatorMode, estimatorInput, reward, goalTime, durationDays]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!startDate) newErrors.start_date = t(locale, 'requiredField');
    if (durationType === 'fixed' && !endDate) newErrors.end_date = t(locale, 'requiredField');
    if (!goal || parseFloat(goal) < 1) newErrors.goal = t(locale, 'requiredField');
    if (!reward || parseFloat(reward) <= 0) newErrors.reward = t(locale, 'requiredField');
    if (showNc && (!nc || parseFloat(nc) < 1)) newErrors.nc = t(locale, 'requiredField');
    if (isWalk && !walkingAreaType.trim()) newErrors.walking_area_type = t(locale, 'requiredField');
    // Only require walking_area when type is 'path'
    if (isWalk && walkingAreaType === 'path' && walkingPaths.length === 0) {
      newErrors.walking_area = t(locale, 'requiredField');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onValidSubmit(
      {
        duration_type: durationType,
        start_date: startDate,
        end_date: durationType === 'fixed' ? endDate : null,
        goal: parseFloat(goal),
        goal_time: goalTime,
        reward: parseFloat(reward),
        nc: showNc ? parseFloat(nc) : undefined,
        walking_area_type: isWalk ? (walkingAreaType as 'open' | 'path') : undefined,
        walking_area: isWalk && walkingAreaType === 'path' ? walkingPaths.map((path, i) => ({
          name_en: `Path ${i + 1}`,
          name_ar: `مسار ${i + 1}`,
          coordinates: path.map(p => [p.lng, p.lat]),
        })) : undefined,
      },
      budgetEstimate.estimatedCp
    );
  };

  const inputClass = (hasError?: boolean) =>
    `w-full px-4 py-3 bg-white dark:bg-gray-700 border ${
      hasError
        ? 'border-red-400 focus:ring-red-400/50 focus:border-red-400'
        : 'border-gray-200 dark:border-gray-600 focus:ring-primary/20 focus:border-primary'
    } rounded-xl focus:ring-2 outline-none transition-all text-gray-800 text-sm`;

  const labelClass = 'block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2';

  const shortcutLabel = (s: DurationShortcut) => {
    if (s === '1m') return t(locale, 'durationOneMonth');
    if (s === '6m') return t(locale, 'durationSixMonths');
    if (s === '1y') return t(locale, 'durationOneYear');
    return t(locale, 'customDuration');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      {/* Main Form */}
      <div className="lg:col-span-7 space-y-8">
        {/* Duration */}
        <section className="space-y-4">
          <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'text-right' : ''}`}>
            {t(locale, 'initiativeDuration')}
          </h3>
          <div className="flex p-1 bg-gray-100 dark:bg-gray-700 rounded-xl w-fit">
            {(['fixed', 'open'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setDurationType(type)}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                  durationType === type
                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white'
                }`}
              >
                {type === 'fixed' ? t(locale, 'fixedDuration') : t(locale, 'openDuration')}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start-date" className={labelClass}>
                {t(locale, 'startDate')} <span className="text-red-500">*</span>
              </label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                min={today()}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputClass(!!errors.start_date)}
              />
              {errors.start_date && (
                <p className="mt-1 text-xs text-red-500">{errors.start_date}</p>
              )}
            </div>
            {durationType === 'fixed' && (
              <div>
                <label className={labelClass}>{t(locale, 'initiativeDuration')}</label>
                <div className="flex gap-2 flex-wrap">
                  {durationShortcuts.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleShortcut(s)}
                      className={`flex-1 py-2.5 px-2 text-xs border rounded-lg transition-all ${
                        activeShortcut === s
                          ? 'border-primary bg-primary/5 text-primary font-bold'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 hover:border-primary hover:text-primary'
                      }`}
                    >
                      {shortcutLabel(s)}
                    </button>
                  ))}
                </div>
                {activeShortcut === 'custom' && (
                  <div className="mt-3">
                    <label htmlFor="end-date" className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                      {t(locale, 'endDate')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="end-date"
                      type="date"
                      value={endDate}
                      min={startDate || today()}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={inputClass(!!errors.end_date)}
                    />
                    {errors.end_date && (
                      <p className="mt-1 text-xs text-red-500">{errors.end_date}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <hr className="border-gray-100 dark:border-gray-700" />

        {/* Impact Structure */}
        <section className="space-y-6">
          <div>
            <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'text-right' : ''}`}>
              {t(locale, 'impactStructure')}
            </h3>
            <p className={`text-sm text-gray-500 dark:text-gray-400 mt-1 ${isRTL ? 'text-right' : ''}`}>
              {t(locale, 'impactStructureSubtitle')}
            </p>
          </div>

          <div>
            <label htmlFor="goal" className={labelClass}>{t(locale, 'targetGoal')}</label>
            <input
              id="goal"
              type="number"
              min={1}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="0"
              className={`${inputClass(!!errors.goal)} text-left`}
            />
            {errors.goal && <p className="mt-1 text-xs text-red-500">{errors.goal}</p>}
          </div>

          <div>
            <div className={`flex justify-between items-center mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <label className={labelClass.replace('mb-2', '')}>{t(locale, 'goalTimeDays')}</label>
              <span className="text-primary font-bold text-sm">{goalTime} {locale === 'ar' ? 'أيام' : 'days'}</span>
            </div>
            <input
              type="range"
              min={1}
              max={30}
              value={goalTime}
              onChange={(e) => setGoalTime(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className={`flex justify-between text-[10px] text-gray-400 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{locale === 'ar' ? '١ يوم' : '1 day'}</span>
              <span>{locale === 'ar' ? '٣٠ يوم' : '30 days'}</span>
            </div>
          </div>

          <div>
            <label htmlFor="reward" className={labelClass}>{t(locale, 'rewardOnCompletion')}</label>
            <div className="relative">
              <span className={`absolute inset-y-0 ${isRTL ? 'right-4' : 'left-4'} flex items-center font-bold text-gray-400 text-sm pointer-events-none`}>
                CP
              </span>
              <input
                id="reward"
                type="number"
                min={0.01}
                step={0.01}
                value={reward}
                onChange={(e) => setReward(e.target.value)}
                placeholder={locale === 'ar' ? 'مثال: 10' : 'e.g. 10'}
                className={`${inputClass(!!errors.reward)} ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
              />
            </div>
            {errors.reward && <p className="mt-1 text-xs text-red-500">{errors.reward}</p>}
          </div>

          {showNc && (
            <div>
              <label htmlFor="nc" className={labelClass}>{t(locale, 'conversionRate')}</label>
              <div className="relative">
                <span className={`absolute inset-y-0 ${isRTL ? 'right-4' : 'left-4'} flex items-center font-bold text-gray-400 text-sm pointer-events-none`}>
                  CP
                </span>
                <input
                  id="nc"
                  type="number"
                  min={1}
                  value={nc}
                  onChange={(e) => setNc(e.target.value)}
                  placeholder={locale === 'ar' ? 'مثال: 10' : 'e.g. 10'}
                  className={`${inputClass(!!errors.nc)} ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                />
              </div>
              {errors.nc && <p className="mt-1 text-xs text-red-500">{errors.nc}</p>}
              <div className={`flex items-center gap-2 mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="material-icons-outlined text-amber-500 text-sm">lightbulb</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t(locale, 'aiRateHint')}{' '}
                  <button type="button" className="text-primary font-bold hover:underline">
                    {t(locale, 'getAiRecommendation')}
                  </button>
                </p>
              </div>
            </div>
          )}

          {isWalk && (
            <div className="space-y-6 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div>
                <label htmlFor="walking-area-type" className={labelClass}>
                  {locale === 'ar' ? 'نوع منطقة المشي' : 'Walking Area Type'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="walking-area-type"
                    value={walkingAreaType}
                    onChange={(e) => setWalkingAreaType(e.target.value)}
                    className={`${inputClass(!!errors.walking_area_type)} appearance-none bg-none pe-10`}
                  >
                    <option value="">{locale === 'ar' ? 'اختر نوعاً' : 'Select type'}</option>
                    <option value="open">{locale === 'ar' ? 'منطقة مفتوحة' : 'Open Area'}</option>
                    <option value="path">{locale === 'ar' ? 'مسار محدد' : 'Defined Path'}</option>
                  </select>
                  <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400">expand_more</span>
                </div>
                {errors.walking_area_type && <p className="mt-1 text-xs text-red-500">{errors.walking_area_type}</p>}
              </div>
              {walkingAreaType === 'path' && (
                <div>
                  <WalkingPathMap
                    paths={walkingPaths}
                    onChange={setWalkingPaths}
                    locale={locale}
                  />
                  {errors.walking_area && <p className="mt-2 text-xs text-red-500">{errors.walking_area}</p>}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Navigation */}
        <div className={`flex gap-4 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            type="button"
            onClick={handleSubmit}
            className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-12 rounded-xl transition-all shadow-lg shadow-primary/20"
          >
            {t(locale, 'createInitiative')}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-bold py-3 px-12 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all"
          >
            {t(locale, 'previous')}
          </button>
        </div>
      </div>

      {/* Budget Estimator Sidebar */}
      <div className="lg:col-span-5">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm">
          <div className="p-6 space-y-6">
            <h3 className="text-base font-bold text-gray-800 text-center">
              {t(locale, 'budgetEstimator')}
            </h3>

            <div className="flex p-1 bg-gray-100 dark:bg-gray-700 rounded-xl">
              <button
                type="button"
                onClick={() => setEstimatorMode('byUsers')}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                  estimatorMode === 'byUsers'
                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t(locale, 'estimateByUsers')}
              </button>
              <button
                type="button"
                onClick={() => { setEstimatorMode('byBudget'); setEstimatorInput(''); }}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                  estimatorMode === 'byBudget'
                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t(locale, 'estimateByBudget')}
              </button>
            </div>

            <div>
              <label htmlFor="estimator-input" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {estimatorMode === 'byUsers' ? t(locale, 'expectedParticipants') : t(locale, 'budgetAmount')}
              </label>
              <input
                id="estimator-input"
                type="number"
                min={0}
                value={estimatorInput}
                onChange={(e) => setEstimatorInput(e.target.value)}
                placeholder={locale === 'ar' ? 'مثال: 5000' : 'e.g. 5000'}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-center font-bold text-base"
              />
            </div>

            <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              {[
                { label: t(locale, 'rewardGivenEvery'), value: goalTime > 0 ? `${goalTime} ${locale === 'ar' ? 'يوم' : 'day'}` : '--' },
                { label: t(locale, 'rewardsPerPeriod'), value: budgetEstimate.periods > 0 ? `${parseFloat(reward) || 0} CP` : '0.00' },
                { label: t(locale, 'initiativeDurationLabel'), value: durationDays > 0 ? `${durationDays} ${locale === 'ar' ? 'يوم' : 'days'}` : '--' },
                { label: t(locale, 'rewardPeriodsCount'), value: budgetEstimate.periods > 0 ? String(budgetEstimate.periods) : '--' },
                {
                  label: estimatorMode === 'byUsers' ? t(locale, 'estimatedBudgetRequired') : t(locale, 'maxParticipants'),
                  value: estimatorMode === 'byUsers'
                    ? (budgetEstimate.estimatedCp > 0 ? `${budgetEstimate.estimatedCp.toLocaleString()} CP` : '--')
                    : (budgetEstimate.maxParticipants > 0 ? String(budgetEstimate.maxParticipants) : '--'),
                  valueClass: 'font-bold text-green-600 dark:text-green-400',
                },
                { label: t(locale, 'estimatedBudgetSar'), value: budgetEstimate.estimatedNc > 0 ? `NC ${budgetEstimate.estimatedNc.toLocaleString()}` : '--', valueClass: 'font-bold' },
              ].map(({ label, value, valueClass }) => (
                <div key={label} className={`flex justify-between items-center text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-gray-500 dark:text-gray-400">{label}</span>
                  <span className={`font-bold text-gray-800 ${valueClass || ''}`}>{value}</span>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 p-4 rounded-xl flex gap-3">
              <span className="material-icons-outlined text-amber-500 shrink-0 text-lg">info</span>
              <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                <strong>{locale === 'ar' ? 'مهم:' : 'Important:'}</strong> {t(locale, 'budgetEstimatorNote')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
